import { AppsEnums, MonthlyReportEnums, StyleEnums } from "models/enums";
import { EmployeeType } from "models/enums/employee";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { WintSuperkollAPIModel, WintSuperkollAPIModelWintSalary } from "wint-api-client/dist/types";
import { AddPaymentMethod } from "./receipt";

/**
 * The monthly-report recipient setting. Lives in Wint.MonthlyReports.Service, not in the Superkoll
 * company settings, so it has its own form rather than riding along on CompanySettings.
 */
export interface MonthlyReportSettings {
  role: MonthlyReportEnums.RecipientRole;
  isEnabled: boolean;
}

export interface CompanySettings {
  name: string;
  orgNumber: string;
  phone?: string;
  mail?: string;
  visitorCity?: string;
  visitorStreet1?: string;
  visitorStreet2?: string;
  visitorZipCode?: string;
  billingCity?: string;
  billingStreet1?: string;
  billingStreet2?: string;
  billingZipCode?: string;
  visitingCountryCode?: string;
  visitingCountryName?: string;
  billingCountryCode?: string;
  billingCountryName?: string;
  companyImage?: UploadedFile[];
}

export interface CustomerInvoiceSettings {
  defaultPaymentTerms?: number;
  invoicePaymentOptions?: PaymentOption[];
  invoiceIBAN?: string;
  invoiceBIC?: string;
  sendInvoiceFromId?: number;
  sendInvoiceFromName?: string;
  companyEmail?: string;
  invoiceTitleTemplate?: string;
  invoiceMessageTemplate?: string;
  invoiceFileNameTemplate?: string;
  reminderCost?: number;
  penaltyInterestType: WintSuperkollAPIModel.InvoicePenaltyInterestType;
  penaltyInterestPercentage: number;
  isAutomaticRemindersEnabled: number;
  automaticReminderDelay?: number;
  automaticReminderAmountLimit?: number;
  automaticReminderPeriod: number;
  automaticReminderCountLimit?: number;
  invoiceReminderNotesTemplate?: string;
  invoiceReminderTitleTemplate?: string;
  invoiceReminderMessageTemplate?: string;
  invoiceReminderFileNameTemplate?: string;
  defaultInvoiceLayout?: WintSuperkollAPIModel.InvoicePdfTemplate;
  defaultNotes?: string;
  homePageUrl?: string;
  useFTax: boolean;
  hideDeliveryDate: boolean;
  units?: InvoiceUnit[];
  invoiceSendType?: WintSuperkollAPIModel.InvoiceSendMethod;
  invoiceSendTypeName?: string;
  invoiceReminderSendType?: WintSuperkollAPIModel.InvoiceSendMethod;
  invoiceReminderSendTypeName?: string;
}

export interface PaymentOption {
  id?: number;
  methodId?: number;
  methodName?: string;
  number?: string;
  isDefault: boolean;
}

export interface InvoiceUnit {
  id?: number;
  name?: string;
}

export interface ProfileSettings {
  name?: string;
  mail?: string;
  ownExpensePaymentMethods?: AddPaymentMethod[];
  bankCardPaymentMethods?: AddPaymentMethod[];
  creditCardPaymentMethods?: AddPaymentMethod[];
  companyCashPaymentMethods?: AddPaymentMethod[];
  settlementPaymentMethods?: AddPaymentMethod[];
  wintCardPaymentMethods?: AddPaymentMethod[];
  phone?: string;
  includeOverdueInvoices?: boolean;
  noDailyMailOnWeekends?: boolean;
  useCompanySpecificEmail?: boolean;
  companySpecificEmails?: WintSuperkollAPIModel.PersonCompanyEmail[];
  profileImage?: UploadedFile[];
  tableSizeId?: number;
  tableSize?: WintSuperkollAPIModel.TableSize;
  tableSizeName?: string;
  languageKey: string;
  languageName: string;
  bankAccount?: string;
  bankAccountClearing?: string;
  additionalBankAccount?: string;
  additionalBankAccountClearing?: string;
}

export interface EmployeesSettings {
  deviationsViaMobile: boolean;
  defaultSalaryDeviationPersonId?: number;
  defaultSalaryDeviationPersonName?: string;
  employees?: WintSuperkollAPIModel.EmployeeItem[];
  isSalaryAdmin?: boolean;
  superkollSalaryType?: WintSuperkollAPIModel.SuperkollSalaryType;
}

export interface EmployeeSettings {
  name?: string;
  id?: number;
  personNumber?: string;
  personNumberFormatted?: string;
  phone?: string;
  mail?: string;
  employeeImage?: UploadedFile[];
  cEO?: boolean;
  contact?: boolean;
  employee?: boolean;
  documents?: boolean;
  incomingInvoicesOverview?: boolean;
  incomingInvoices?: boolean;
  invoicesAdmin?: boolean;
  invoices?: boolean;
  receiptsAdmin?: boolean;
  receipts?: boolean;
  admin?: boolean;
  reports?: boolean;
  incomingInvoicesAdmin?: boolean;
  incomingInvoicesSignee?: boolean;
  agreement?: boolean;
  cashReconciliation?: boolean;
  paymentFileSignee?: boolean;
  missingDocumentAdmin?: boolean;
  uploadGenericDocuments?: boolean;
  auditor?: boolean;
  revisor?: boolean;
  salaryAdmin?: boolean;
  deviationAdmin?: boolean;
  paymentSignee?: boolean;
  stoppedPaymentAdmin?: boolean;
  advisor?: boolean;
  board?: boolean;
  bankAccount?: string;
  bankAccountClearing?: string;
  taxTable?: WintSuperkollAPIModel.TaxTableInfo;
  taxTableName?: string;
  employmentStartDate?: Date;
  employmentEndDate?: Date;
  terminationReason?: WintSuperkollAPIModel.EmploymentTerminationReason;
  terminationReasonName?: string;
  canChangeTerminationReason?: boolean;
  monthlySalary?: number;
  hourlySalary?: number;
  employmentRate?: number;
  scheduleDescription?: string;
  schedule?: WintSuperkollAPIModelWintSalary.ScheduleWeek[];
  futureSchedule?: WintSuperkollAPIModelWintSalary.ScheduleWeek[];
  futureEmploymentRate?: number;
  futureUsualWorkHours?: number;
  scheduleViewMode?: "current" | "scheduled";
  additionalBankAccount?: string;
  additionalBankAccountClearing?: string;
  assistants?: WintSuperkollAPIModel.EmployeeChildAssistant[];
  quickSelection?: EmployeeType;
  salaryActive?: boolean;
  salaryActivated?: boolean;
  salaryChangeFrom?: Date;
  workplaceStreetAddress?: string;
  workplaceCity?: string;
  employmentRateChangeFrom?: Date;
  isTimeReportingMandatory?: boolean;
  timeReportingInterval?: WintSuperkollAPIModel.TimeReportingInterval;
  timeReportingIntervalName?: string;
  incomingInvoiceReferenceNames?: WintSuperkollAPIModel.IncomingInvoiceReferenceName[];
  businessUnitId?: string;
  businessUnitName?: string;
  position?: string;
  vacationConditions?: number;
  occupationalCategorisationId?: WintSuperkollAPIModel.EmployeeWorkType;
  occupationalCategorisation?: string;
  usualWorkHours?: number;
  salaryCostCenter?: string[];
  wintSalarySpecificSettings?: WintSuperkollAPIModelWintSalary.WintSalarySpecificSettings;
  wintSalaryType?: string;
  wintSalaryTypeDescription?: string;
  userTypeId?: number;
}

export interface RemoveEmployee {
  employmentEndDate?: Date;
}

export interface DimensionSettings extends Record<string, any> {
  bookDefaultArticleDimensions: boolean;
}

export interface BaseDimension extends WintSuperkollAPIModel.DimensionItem {
  key?: number | string;
}

export interface DimensionGroup {
  fieldName: string;
  dimensionTypeId: string;
  dimensionTypeName: string;
  dimensionList: BaseDimension[];
}

export interface StandardDimension {
  name: string;
  dimensionId: string;
  dimensionName: string;
  isDemanded: boolean;
  category: StandardDimensionCategory;
}

export enum StandardDimensionCategory {
  Invoice = 0,
  IncomingInvoice = 1,
  Receipt = 2,
}

export interface IntegrationSettings {
  apps: IntegrationItem[];
  canSave: boolean;
}

export interface IntegrationItem {
  name?: string;
  appCategory: AppsEnums.AppCategory;
  appCategoryName?: string;
  description?: string;
  imageSource?: string;
  isActive?: boolean;
  appData?: any;
  activationState?: WintSuperkollAPIModel.AppActivationState;
  displayOrder?: number;
  stripeConnectAccountId?: string;
  statusLabel?: string;
  statusDisplayType?: StyleEnums.DisplayType;
  statusUnavailable?: boolean;
  vismaAdvisorStatus?: string;
  vismaAdvisorAgencyName?: string;
}

export interface BankBalanceSettings {
  accounts: BankBalanceAccountSetting[];
}

export interface BankBalanceAccountSetting {
  accountId: number;
  accountNumber: number;
  bankName?: string;
  bankAccountNumber?: string;
  showOnDashboard: boolean;
}

export interface SalarySettings {
  salarySettings: SalarySetting[];
}

export interface SalarySetting {
  id: number;
  name?: string;
  bankAccount?: string;
  bankAccountClearing?: string;
  monthlySalary?: number;
  employmentRate?: number;
  taxTable?: WintSuperkollAPIModel.TaxTableInfo;
  taxTableName?: string;
  employmentStartDate?: Date;
  employmentEndDate?: Date;
  salaryActive?: boolean;
  salaryChangeFrom?: Date;
  salaryChanged?: boolean;
  workplaceStreetAddress?: string;
  workplaceCity?: string;
  salaryActivated?: boolean;
  position?: string;
  vacationConditions?: number;
  workType?: WintSuperkollAPIModel.EmployeeWorkType;
  usualWorkHours?: number;
  schedule?: string;
}
